// Months, weeks or trading days at a glance: three stacked panels that share one time axis, net
// P&L as a smooth line (green above $0, red below), win rate and trade count as bars. Each panel
// has its own scale, so no number is read against another one's axis. Hover or tab to a period
// for all three numbers; the corner button opens it full screen with a table view. The numbers
// come from periodStats.js.
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { periodRows, smoothPath } from './periodStats';
import { formatMoney, formatMoneyCompact } from './pnl';
import { useI18n } from './i18n';

const UNITS = [['day', 'Days'], ['week', 'Weeks'], ['month', 'Months']];
const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');

// A bar with rounded top corners (or bottom corners when it hangs below the line).
function barPath(x, y, w, h, r) {
  if (h <= 0) return '';
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
}

function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

function Panels({ rows, width, big }) {
  const { t } = useI18n();
  const id = useId().replace(/:/g, '');
  const [hover, setHover] = useState(null);
  const H = big ? { pnl: 240, win: 96, count: 96 } : { pnl: 130, win: 52, count: 52 };
  const padL = 52;
  const padR = 10;
  const gap = 32;
  const plotW = Math.max(40, width - padL - padR);
  const top1 = 20;
  const top2 = top1 + H.pnl + gap;
  const top3 = top2 + H.win + gap;
  const axisY = top3 + H.count;
  const height = axisY + 22;
  const bw = plotW / rows.length;
  const cx = (i) => padL + bw * (i + 0.5);
  const barW = Math.max(3, Math.min(bw * 0.58, big ? 34 : 24));

  const nets = rows.map((r) => r.net);
  let lo = Math.min(0, ...nets);
  let hi = Math.max(0, ...nets);
  if (hi === lo) { hi += 1; lo -= 1; }
  const yP = (v) => top1 + ((hi - v) / (hi - lo)) * H.pnl;
  const zero = yP(0);
  const points = rows.map((r, i) => [cx(i), yP(r.net)]);
  const line = smoothPath(points);
  const area = rows.length > 1 ? `${line} L${points[points.length - 1][0]},${zero} L${points[0][0]},${zero} Z` : '';
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  const every = Math.max(1, Math.ceil(rows.length / Math.max(2, Math.floor(plotW / (big ? 64 : 46)))));
  const h = hover === null ? null : rows[hover];

  return (
    <div className="pp-plot" onPointerLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label={t('Net P&L, win rate and trades by period')} className="pp-svg">
        <defs>
          <linearGradient id={`g${id}`} gradientUnits="userSpaceOnUse" x1="0" y1={top1} x2="0" y2={zero}>
            <stop offset="0" stopColor="var(--gain)" stopOpacity="0.38" /><stop offset="1" stopColor="var(--gain)" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id={`r${id}`} gradientUnits="userSpaceOnUse" x1="0" y1={zero} x2="0" y2={top1 + H.pnl}>
            <stop offset="0" stopColor="var(--loss)" stopOpacity="0.02" /><stop offset="1" stopColor="var(--loss)" stopOpacity="0.38" />
          </linearGradient>
          <clipPath id={`above${id}`}><rect x="0" y="0" width={width} height={zero} /></clipPath>
          <clipPath id={`below${id}`}><rect x="0" y={zero} width={width} height={height} /></clipPath>
        </defs>

        {/* Net P&L */}
        <text className="pp-title" x={padL} y={top1 - 8}>{t('Net P&L')}</text>
        {[hi, lo].filter((v) => v !== 0).map((v) => (
          <g key={v}><line className="pp-grid" x1={padL} x2={width - padR} y1={yP(v)} y2={yP(v)} /><text className="pp-tick" x={padL - 6} y={yP(v) + 4}>{formatMoneyCompact(v)}</text></g>
        ))}
        <line className="pp-zero" x1={padL} x2={width - padR} y1={zero} y2={zero} />
        <text className="pp-tick" x={padL - 6} y={zero + 4}>$0</text>
        {area && <path className="pp-area" d={area} fill={`url(#g${id})`} clipPath={`url(#above${id})`} />}
        {area && <path className="pp-area" d={area} fill={`url(#r${id})`} clipPath={`url(#below${id})`} />}
        <path className="pp-line" d={line} pathLength="1" stroke="var(--gain)" clipPath={`url(#above${id})`} />
        <path className="pp-line" d={line} pathLength="1" stroke="var(--loss)" clipPath={`url(#below${id})`} />
        {points.map(([x, y], i) => rows[i].count > 0 && (
          <circle key={rows[i].key} className={`pp-dot ${tone(rows[i].net)}`} cx={x} cy={y} r={hover === i ? 5 : 3} />
        ))}

        {/* Win rate */}
        <text className="pp-title" x={padL} y={top2 - 8}>{t('Win rate')}</text>
        <line className="pp-grid" x1={padL} x2={width - padR} y1={top2 + H.win / 2} y2={top2 + H.win / 2} />
        <text className="pp-tick" x={padL - 6} y={top2 + H.win / 2 + 4}>50%</text>
        <text className="pp-tick" x={padL - 6} y={top2 + 4}>100%</text>
        <line className="pp-base" x1={padL} x2={width - padR} y1={top2 + H.win} y2={top2 + H.win} />
        {rows.map((r, i) => r.winRate !== null && (
          <path key={r.key} className={`pp-bar win${hover === i ? ' on' : ''}`} style={{ '--i': i }}
            d={barPath(cx(i) - barW / 2, top2 + H.win - (r.winRate / 100) * H.win, barW, (r.winRate / 100) * H.win, 4)} />
        ))}

        {/* Trades */}
        <text className="pp-title" x={padL} y={top3 - 8}>{t('Trades')}</text>
        <text className="pp-tick" x={padL - 6} y={top3 + 4}>{maxCount}</text>
        <line className="pp-base" x1={padL} x2={width - padR} y1={axisY} y2={axisY} />
        {rows.map((r, i) => r.count > 0 && (
          <path key={r.key} className={`pp-bar count${hover === i ? ' on' : ''}`} style={{ '--i': i }}
            d={barPath(cx(i) - barW / 2, axisY - (r.count / maxCount) * H.count, barW, (r.count / maxCount) * H.count, 4)} />
        ))}

        {rows.map((r, i) => (i % every === 0 || i === rows.length - 1) && (rows.length - 1 - i >= every || i === rows.length - 1) && (
          <text key={r.key} className="pp-x" x={cx(i)} y={axisY + 16}>{r.label}</text>
        ))}

        {hover !== null && <line className="pp-cross" x1={cx(hover)} x2={cx(hover)} y1={top1} y2={axisY} />}
        {rows.map((r, i) => (
          <rect key={r.key} className="pp-hit" x={padL + bw * i} y={0} width={bw} height={axisY} tabIndex={0}
            aria-label={`${r.longLabel}: ${formatMoney(r.net)}, ${r.winRate === null ? '—' : `${Math.round(r.winRate)}%`}, ${t(r.count === 1 ? '{n} trade' : '{n} trades', { n: r.count })}`}
            onPointerEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} />
        ))}
      </svg>
      {h && (
        <div className={`pp-tip${cx(hover) > width * 0.62 ? ' left' : ''}`} style={{ left: cx(hover) }}>
          <span className="pp-tip-head">{h.longLabel}</span>
          <span><strong className={tone(h.net)}>{formatMoney(h.net)}</strong> <small>{t('Net P&L')}</small></span>
          <span><strong>{h.winRate === null ? '—' : `${Math.round(h.winRate)}%`}</strong> <small>{t('Win rate')}</small></span>
          <span><strong>{h.count}</strong> <small>{t('Trades')}</small></span>
        </div>
      )}
    </div>
  );
}

function Chart({ trades, unit, big }) {
  const ref = useRef(null);
  const width = useWidth(ref);
  const rows = useMemo(() => periodRows(trades, unit), [trades, unit]);
  return (
    <div ref={ref} className="pp-wrap pp-anim">
      {width > 0 && rows.length > 0 && <Panels rows={rows} width={width} big={big} />}
    </div>
  );
}

function UnitPicker({ unit, setUnit, units }) {
  const { t } = useI18n();
  if (units.length < 2) return null;
  return (
    <div className="pp-units" role="group" aria-label={t('Show by')}>
      {UNITS.filter(([u]) => units.includes(u)).map(([u, label]) => (
        <button key={u} type="button" className={`secondary small${unit === u ? ' active' : ''}`} aria-pressed={unit === u} onClick={() => setUnit(u)}>{t(label)}</button>
      ))}
    </div>
  );
}

export default function PeriodPanels({ trades, unit: startUnit = 'month', units = ['day', 'week', 'month'], title, note, adminPreview = false }) {
  const { t } = useI18n();
  // Start on the given unit, or a finer one when it would show fewer than 3 periods (the finest
  // when none has 3). Worked out
  // again when trades arrive, until the trader picks a unit themselves.
  const autoUnit = useMemo(() => {
    const order = ['month', 'week', 'day'].filter((u) => units.includes(u));
    return order.slice(Math.max(0, order.indexOf(startUnit))).find((u) => periodRows(trades, u).length >= 3) || order[order.length - 1] || startUnit;
  }, [trades, units.join(), startUnit]); // eslint-disable-line react-hooks/exhaustive-deps
  const [picked, setPicked] = useState(null);
  const unit = picked || autoUnit;
  const setUnit = setPicked;
  const [open, setOpen] = useState(false);
  const [table, setTable] = useState(false);
  const rows = useMemo(() => periodRows(trades, unit), [trades, unit]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!trades.some((x) => x.date)) return null;
  const head = (
    <div className="pp-head">
      <div><strong>{title || t('At a glance')}</strong>{adminPreview && <span className="admin-preview-badge">Admin only</span>}{note && <small>{note}</small>}</div>
      <UnitPicker unit={unit} setUnit={setUnit} units={units} />
    </div>
  );
  return (
    <figure className={`pp${adminPreview ? ' admin-preview-card' : ''}`}>
      {head}
      <button type="button" className="pp-expand icon-button" aria-label={t('Enlarge chart')} title={t('Enlarge chart')} onClick={() => setOpen(true)}>
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <Chart key={unit} trades={trades} unit={unit} />
      {open && createPortal(
        <div className="pp-modal" role="dialog" aria-modal="true" aria-label={title || t('At a glance')} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="pp-modal-box">
            <div className="pp-modal-top">
              {head}
              <div className="pp-modal-actions">
                <button type="button" className="secondary small" onClick={() => setTable((v) => !v)}>{table ? t('Show chart') : t('Show table')}</button>
                <button type="button" className="icon-button" aria-label={t('Close')} onClick={() => setOpen(false)}>&times;</button>
              </div>
            </div>
            {table ? (
              <div className="table-wrap">
                <table className="biz-table">
                  <thead><tr><th>{t('Period')}</th><th className="num">{t('Net P&L')}</th><th className="num">{t('Win rate')}</th><th className="num">{t('Trades')}</th></tr></thead>
                  <tbody>{[...rows].reverse().map((r) => (
                    <tr key={r.key}><th scope="row">{r.longLabel}</th><td className={`num ${tone(r.net)}`}>{formatMoney(r.net)}</td><td className="num">{r.winRate === null ? '—' : `${Math.round(r.winRate)}%`}</td><td className="num">{r.count}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <Chart key={unit} trades={trades} unit={unit} big />}
          </div>
        </div>,
        document.body,
      )}
    </figure>
  );
}
